import { FeesRepo } from "@services/fees.repo";
import * as _ from "lodash";
import * as moment from "moment";
import * as echarts from "echarts";
import { FeeCalculationType } from "../../fees";
import { extractErrorMessage } from "@common/utils/errorUtils";

class FeeTypeFeesController implements angular.IOnInit {
  public feesByFeeTypesLimit: number = 10;
  public topFeeTypesFeeChart: any;
  public topFeeTypesFeeChartOptions: any;
  public isLoadingFeeTypesFees: boolean = false;
  public topFeeTypesFees: Array<any>;
  public feeTypesFeeDate: Date;
  public topFeeTypesFeesGridOptions: any;
  public sortTopBottomFeeTypesFeesByNav: boolean = false;
  public selectedFeeCalculationTypeFeeTypesFees: FeeCalculationType = FeeCalculationType.COMBINED;

  // Bindings from parent
  public lastMonth: Date;
  public selectedCurrency: string;
  public feeCalulationTypes: Array<any>;
  public feeTypeFeesRef: any;

  // Callback to update parent with fee types data
  public onFeeTypesUpdate: (data: Array<any>) => void;

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private notifyjs: any,
    private $scope: angular.IScope
  ) {}

  $onInit() {
    this.topFeeTypesFeeChart = echarts.init(
      document.getElementById("top-fee-types-fee-value-chart")
    );
    this.$scope.$watch(
      () =>
        this.topFeeTypesFeeChart.getDom().parentElement.parentElement
          .clientHeight,
      this.topFeeTypesFeeChart.resize()
    );

    this.topFeeTypesFeesGridOptions = this.$scope[
      "topFeeTypesFeesGridOptions"
    ] = {
      data: [],
      urlSync: false,
    };

    // Watch for currency changes and update fees
    this.$scope.$watch(
      () => this.selectedCurrency,
      (newVal, oldVal) => {
        if (newVal !== oldVal && newVal) {
          this.updateFeeTypeFees();
        }
      }
    );

    this.updateFeeTypeFees();
  }

  updateFeeTypeFees = () => {
    this.getTopFeeTypesByLimit();
  };

  formatDate(selectedDate) {
    return moment(selectedDate).endOf("month").format("YYYY-MM");
  }

  getTopFeeTypesByLimit = () => {
    this.isLoadingFeeTypesFees = true;
    if (this.feeTypeFeesRef) {
      this.feeTypeFeesRef.isLoading = true;
    }
    this.feesRepo
      .getFeeTypesFeesByLimit(
        this.feeTypesFeeDate,
        this.feesByFeeTypesLimit,
        true,
        this.selectedCurrency,
        this.selectedFeeCalculationTypeFeeTypesFees,
        this.sortTopBottomFeeTypesFeesByNav
      )
      .then((res) => {
        this.topFeeTypesFees = res.data as Array<any>;
        this.getTopFeeTypesFeeGraph();
        this.topFeeTypesFeesGridOptions.data = this.topFeeTypesFees;
        if (this.onFeeTypesUpdate) {
          this.onFeeTypesUpdate(this.topFeeTypesFees );
        }
      })
      .catch((err) => {
        this.notifyjs("Failed to get top client fees " + extractErrorMessage(err));
      })
      .finally(() => {
        this.isLoadingFeeTypesFees = false;
        if (this.feeTypeFeesRef) {
          this.feeTypeFeesRef.isLoading = false;
        }
      });
  };

  normaliseNumbers = (labelValue) => {
    // Nine Zeroes for Billions
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? Math.abs(Number(labelValue)) / 1.0e9 + "B"
      : // Six Zeroes for Millions
      Math.abs(Number(labelValue)) >= 1.0e6
      ? Math.abs(Number(labelValue)) / 1.0e6 + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(labelValue)) >= 1.0e3
      ? Math.abs(Number(labelValue)) / 1.0e3 + "K"
      : Math.abs(Number(labelValue));
  };

  getTopFeeTypesFeeGraph = () => {
    let feeTypeNames = this.topFeeTypesFees.map(
      (feeType) => feeType.feeTypeName
    );
    let feeValues;
    if (this.sortTopBottomFeeTypesFeesByNav) {
      feeValues = this.topFeeTypesFees.map((feeType) =>
        feeType.marketValue.toFixed(2)
      );
    } else {
      feeValues = this.topFeeTypesFees.map((feeType) =>
        feeType.feeValue.toFixed(2)
      );
    }

    this.topFeeTypesFeeChartOptions = {
      title: {
        text: `Top ${feeTypeNames.length} Fee Types ${
          this.sortTopBottomFeeTypesFeesByNav ? "NAVs" : "Fees"
        }`,
        textStyle: {
          fontFamily: "Seravek",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          let feeParam = this.topFeeTypesFees.filter(
            (feeParam) => feeParam.feeTypeName == params[0].name
          )[0];
          return `  Fee Type : ${feeParam.feeTypeName} <br>
                    Fee Value : ${feeParam.feeValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} <br>
                    NAV : ${feeParam.marketValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} `;
        },
      },

      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: "Save as Image",
          },
          dataZoom: {
            yAxisIndex: "none",
            title: {
              zoom: "Zoom In",
              back: "Back",
            },
          },
          restore: {
            title: "Restore",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: feeTypeNames,
        barGap: 0,
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: [
        {
          type: "value",
          name: `${this.sortTopBottomFeeTypesFeesByNav ? "NAVs" : "Fees"} `,

          axisLabel: {
            formatter: (params) => {
              return params > 0
                ? `${this.selectedCurrency} ${this.normaliseNumbers(params)}`
                : "";
            },
          },
        },
      ],
      series: [
        {
          type: "bar",
          name: "Fee Value",
          data: feeValues,
        },
      ],
    };

    this.topFeeTypesFeeChart.setOption(this.topFeeTypesFeeChartOptions);
  };
}

export const feeTypeFees: angular.IComponentOptions = {
  template: require("./fee-type-fees.html"),
  controller: FeeTypeFeesController,
  bindings: {
    lastMonth: "<",
    selectedCurrency: "<",
    feeCalulationTypes: "<",
    feeTypesFeeDate: "=",
    onFeeTypesUpdate: "&",
    feeTypeFeesRef: "=?",
  },
};




// WEBPACK FOOTER //
// ./src/app/fees/fees-components/fee-type-fees/fee-type-fees.ts